import type { FieldValues, Path } from 'react-hook-form';
import { Box } from '@mui/material';
import { RhfSelect } from '@core/components';
import type { StoleCut } from '../../api/stoleCut';
import { MaterialKind } from '../enum/materialKind';
import { OptionalFabricColorField } from '../pages/OrderForm/OptionalFabricColorField';
import { OptionalMaterialField } from '../pages/OrderForm/OptionalMaterialField';
import { RhfColorSelect } from '../pages/OrderForm/RhfColorSelect';
import { RhfFabricSelect } from '../pages/OrderForm/RhfFabricSelect';

const rowSx = {
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  gap: 2,
} as const;

type StoleCutValues = FieldValues & { stoleCut: string };
type StolePanelValues = FieldValues & {
  panelFabric: string;
  panelColor: string;
};
type StoleOptionValues = FieldValues & {
  withCord: boolean;
  cord: string;
  withTassel: boolean;
  tassel: string;
  withPatching: boolean;
  patchingFabric: string;
  patchingColor: string;
};

function StoleCutField<T extends StoleCutValues>({
  cuts,
}: {
  cuts: StoleCut[];
}) {
  return (
    <RhfSelect<T, StoleCut>
      name={'stoleCut' as Path<T>}
      label="Type"
      items={cuts}
      getOptionValue={(cut) => String(cut.id)}
      getOptionLabel={(cut) => cut.name}
    />
  );
}

function StolePanelFabricFields<T extends StolePanelValues>() {
  return (
    <Box sx={rowSx}>
      <RhfFabricSelect<T>
        name={'panelFabric' as Path<T>}
        label="Fabric"
        flag="isStolePanel"
      />
      <RhfColorSelect<T>
        name={'panelColor' as Path<T>}
        label="Color"
        fabricFieldName={'panelFabric' as Path<T>}
      />
    </Box>
  );
}

export function StoleOptionFields<T extends StoleOptionValues>({
  patching,
}: {
  patching: boolean;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <OptionalMaterialField<T>
        withName={'withCord' as Path<T>}
        materialName={'cord' as Path<T>}
        kind={MaterialKind.Cord}
      />
      <OptionalMaterialField<T>
        withName={'withTassel' as Path<T>}
        materialName={'tassel' as Path<T>}
        kind={MaterialKind.Tassel}
      />
      {patching ? (
        <OptionalFabricColorField<T>
          label="Patching"
          withName={'withPatching' as Path<T>}
          fabricName={'patchingFabric' as Path<T>}
          colorName={'patchingColor' as Path<T>}
          fabricFlag="isStolePatching"
        />
      ) : null}
    </Box>
  );
}

export function StolePanelFields<T extends StoleCutValues & StolePanelValues>({
  hasCuts,
  cuts,
}: {
  hasCuts: boolean;
  cuts: StoleCut[];
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {hasCuts ? <StoleCutField<T> cuts={cuts} /> : null}
      <StolePanelFabricFields<T> />
    </Box>
  );
}
